import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  Signal,
  booleanAttribute,
  computed,
  effect,
  forwardRef,
  inject,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { Subscription, combineLatest, shareReplay } from 'rxjs';
import { debounceTime, filter, switchMap } from 'rxjs/operators';
import { SEARCH_DEBOUNCE_MS } from '../constants';
import {
  GalaxyAbstractFilterInput,
  GalaxyFilterValueInterface,
  InnerGalaxyFilterChipInjectionToken,
} from '../interface';

@Component({
  selector: 'glxy-string-filter-input',
  template: `
    <glxy-form-field class="filter-string-input" [bottomSpacing]="'none'">
      <input
        [placeholder]="'GALAXY.FILTER.CHIP.SELECT_EXPRESSION_PLACEHOLDER' | translate"
        type="text"
        matInput
        [formControl]="formControl"
        [matAutocomplete]="auto"
        data-testid="filter-text-input"
      />
      <mat-autocomplete #auto="matAutocomplete">
        <mat-option *ngFor="let option of filteredOptions()" [value]="option">
          {{ option }}
        </mat-option>
      </mat-autocomplete>
    </glxy-form-field>
  `,
  providers: [{ provide: GalaxyAbstractFilterInput, useExisting: forwardRef(() => GalaxyStringFilterInputComponent) }],
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    GalaxyFormFieldModule,
    MatAutocompleteModule,
  ],
})
export class GalaxyStringFilterInputComponent implements GalaxyAbstractFilterInput, OnDestroy {
  @Input() set fieldId(value: string) {
    this._fieldId.set(value);
  }
  @Output() valueChanges = new EventEmitter<GalaxyFilterValueInterface[]>();
  formControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });

  searchTerm = toSignal(this.formControl.valueChanges, { initialValue: '' });
  _fieldId = signal('');
  availableOptions = signal<string[]>([]);

  @Input() set initialValue(value: GalaxyFilterValueInterface[]) {
    if (value?.length > 0 && value[0].string) {
      this.formControl.setValue(value[0].string);
    }
  }
  @Input({ transform: booleanAttribute }) set disabled(value: boolean) {
    if (value) {
      this.formControl.disable();
    }
  }

  subscriptions: Subscription[] = [];
  filteredOptions: Signal<string[]> = computed(() => this.availableOptions());

  private readonly filtersDependencies = inject(InnerGalaxyFilterChipInjectionToken);
  constructor() {
    effect(() => {
      const value = this.searchTerm();
      if (value === '') {
        this.valueChanges.emit([]);
        return;
      }
      this.valueChanges.emit([{ string: value }]);
    });

    const searchTerm$ = toObservable(this.searchTerm);
    const fieldId$ = toObservable(this._fieldId);

    this.subscriptions.push(
      combineLatest([searchTerm$, fieldId$])
        .pipe(
          filter(([, fieldId]) => !!fieldId),
          debounceTime(SEARCH_DEBOUNCE_MS),
          switchMap(([searchTerm, fieldId]) => this.filtersDependencies.getFieldOptions(fieldId, searchTerm)),
          shareReplay(1),
        )
        .subscribe((options) => this.availableOptions.set(options)),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  clearValue() {
    this.formControl.setValue('');
  }
}
